import { FC, HTMLAttributes } from "react";
import clsx from "clsx";

import CTAButton from "@/components/atoms/buttons/CTAButton";
import { ITextButton } from "@/components/atoms/buttons/types";

// Three-tier button rule (orange → primary blue → outlined).
// CallCtaSection sits on a dark gradient band, so the "outline-brand" tertiary
// uses the dark-bg-friendly "outline" variant (white border + white text)
// instead of the brand-blue "outline-brand" we use on light backgrounds.
const variantFor = (idx: number): "orange" | "primary" | "outline" =>
  idx === 0 ? "orange" : idx === 1 ? "primary" : "outline";

// ----------------------------------------------------------------------------
// Call-to-action section — centered card on a brand gradient with a subtle
// radial glow + canonical clamp typography. Buttons render through
// CTAButton so they pick up the standard arrow icon + hover lift used
// everywhere else on the site, while still honoring the variant the
// Strapi editor chose (yellow / outline / primary / etc.).
// ----------------------------------------------------------------------------

type ICallCtaSectionProps = HTMLAttributes<HTMLDivElement> & {
  heading?: string;
  description?: string;
  buttons?: ITextButton[];
};

const CallCtaSection: FC<ICallCtaSectionProps> = ({
  heading,
  description,
  buttons,
  className,
  ...other
}) => {
  return (
    <section
      className={clsx(
        // Full-bleed band — no `container` wrapper so the gradient
        // spans the entire viewport. Inner content is constrained for
        // readability via max-w-3xl + container padding.
        "relative isolate w-full overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 py-8 md:py-10 lg:py-12",
        className,
      )}
      {...other}
    >
      {/* Floating "aurora" orbs — sized to match the tighter section
          height so they fill the visible band without dominating it.
          Three offset timings (18s / 22s / 26s) keep the motion fluid.
          Transform-only → GPU accelerated.
          NOTE: each orb is wrapped in a positioning div with the absolute
          + top/left utilities; the inner div carries the animation.
          Doing both on the same element makes Tailwind's translate-x/y
          utilities and the keyframe's `transform: translate(...)` overwrite
          each other, killing the motion (centered orb) or shifting the
          orb to the wrong spot (off-screen orbs). */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -top-16"
      >
        <div className="h-72 w-72 animate-float-a rounded-full bg-yellow-400/50 blur-3xl" />
      </div>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-20 -left-16"
      >
        <div className="h-80 w-80 animate-float-b rounded-full bg-secondary-900/60 blur-3xl" />
      </div>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      >
        <div className="h-56 w-56 animate-float-c rounded-full bg-cyan-400/25 blur-3xl" />
      </div>

      <div className="container relative mx-auto">
        <div className="mx-auto max-w-3xl text-center">
          {heading && (
            <h2 className="font-bricolage-grotesque text-[clamp(1.25rem,1.5vw+0.875rem,1.875rem)] font-medium !leading-[1.2] text-white">
              {heading}
            </h2>
          )}

          {description && (
            <p className="mx-auto mt-2 max-w-2xl text-[clamp(0.875rem,0.3vw+0.8rem,1rem)] leading-relaxed text-white/80">
              {description}
            </p>
          )}

          {buttons && buttons.length > 0 && (
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              {buttons.map((b, idx) => (
                <CTAButton
                  key={b.id ?? idx}
                  id={b.id}
                  href={b.link}
                  target={b.target}
                  variant={variantFor(idx)}
                  radius="full"
                  size="md"
                  showIcon
                  iconType="arrow"
                  iconPosition="right"
                >
                  {b.text}
                </CTAButton>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CallCtaSection;
